import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
    connectSocket,
    joinCourseRoom,
    leaveCourseRoom,
} from '../services/socket';
import {
    addMessage,
    addOnlineUser,
    removeOnlineUser,
    setOnlineUsers,
    addTypingUser,
    removeTypingUser,
    updateUnreadCount,
} from '../redux/reducers/chatRealtime';

/**
 * Escucha los eventos del chat del curso. Las notificaciones (`notification_new`)
 * las maneja `useNotificationsFeed` a nivel de app para el centro del nav.
 */
export const useChatSocket = (courseId, user) => {
    const dispatch = useDispatch();
    const userId = user?._id;

    useEffect(() => {
        if (!courseId || !userId) return undefined;

        let socketInstance = null;
        let mounted = true;

        const setupListeners = (socket) => {
            const onConnect = () => {
                if (!mounted) return;
                joinCourseRoom(courseId);
            };

            const onReceiveMessage = (message) => {
                if (!mounted) return;
                dispatch(addMessage(message));
            };

            const onUserOnline = (data) => {
                if (!mounted) return;
                if (data.onlineUsers) {
                    dispatch(setOnlineUsers(data.onlineUsers));
                } else if (data.user) {
                    dispatch(addOnlineUser(data.user));
                }
            };

            const onUserOffline = ({ userId }) => {
                if (!mounted) return;
                dispatch(removeOnlineUser(userId));
            };

            const onTypingStart = (data) => {
                if (!mounted) return;
                dispatch(addTypingUser(data));
            };

            const onTypingStop = (data) => {
                if (!mounted) return;
                dispatch(removeTypingUser(data));
            };

            const onUnreadUpdate = (data) => {
                if (!mounted) return;
                dispatch(updateUnreadCount({
                    conversationId: data.conversationId,
                    unreadCount: data.unreadCount ?? 0,
                }));
            };

            socket.on('connect', onConnect);
            socket.on('receive_message', onReceiveMessage);
            socket.on('user_online', onUserOnline);
            socket.on('user_offline', onUserOffline);
            socket.on('typing_start', onTypingStart);
            socket.on('typing_stop', onTypingStop);
            socket.on('unread_update', onUnreadUpdate);

            if (socket.connected) {
                onConnect();
            }

            return () => {
                try {
                    socket?.off?.('connect', onConnect);
                    socket?.off?.('receive_message', onReceiveMessage);
                    socket?.off?.('user_online', onUserOnline);
                    socket?.off?.('user_offline', onUserOffline);
                    socket?.off?.('typing_start', onTypingStart);
                    socket?.off?.('typing_stop', onTypingStop);
                    socket?.off?.('unread_update', onUnreadUpdate);
                } catch {
                    // el socket ya se cerró al salir del curso
                }
            };
        };

        const init = async () => {
            try {
                socketInstance = await connectSocket();
                if (!mounted || !socketInstance) return;
                const cleanupListeners = setupListeners(socketInstance);
                return cleanupListeners;
            } catch (err) {
                console.warn('[Socket] No conectado:', err.message);
            }
        };

        let cleanupListeners = null;
        init().then((cleanup) => {
            cleanupListeners = cleanup;
        });

        return () => {
            mounted = false;
            leaveCourseRoom(courseId);
            if (cleanupListeners) cleanupListeners();
        };
    }, [courseId, userId, dispatch]);
};
